/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {CCInbox, MessageInfo_GetUnread} from "@app/ir-q-app-common/types/message-api";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import * as React from "react";
import {useContext} from "react";
import {ICONS} from "@res/icons";
import {hideTooltip, showTooltipAtXY} from "../../../widget/appTooltip";
import {Badge} from "../../../widget/Badge";
import * as emotion from "emotion";
import {messagingTableRowHeight} from "../Page_Messages";
import {backgroundCell} from "../InboxTable";
import {ChatsContext} from "../ChatsContext";
import {ContactsContext} from "../ContactsContext";

const UnreadDot = emotion.css(
    {
        height: 18,
        width: 18,
        backgroundColor: COLORS.secondary_red,
        borderRadius: "100%"
    }
);

type Props = {
    chat: CCInbox | MessageInfo_GetUnread
    isOpen: boolean
    getUnreadMessages: () => number
    markAsRead: () => void
    markAsUnread: () => void
}

export const MarkAsReadIcon = (props: Props) => {
    const {messages} = useContext(ChatsContext);
    const {contacts} = useContext(ContactsContext);

    const hideIcon = () => {
        const _unreadMessages = props.getUnreadMessages();
        if (_unreadMessages > 0)
            return false;

        const lastMessage = props.chat.lastMessage;
        if (!lastMessage)
            return true;

        const isSentFromUnit = contacts[lastMessage.senderId]?.contactType === "agentUser";
        if (isSentFromUnit)
            return false;

        if (messages && messages[props.chat._id]?.length && messages[props.chat._id].find(msg => contacts[msg.senderId]?.contactType === "agentUser"))
            return false;

        return true;
    }

    const showMarkAsReadTooltip = (e: React.MouseEvent) => {
        const _unreadMessages = props.getUnreadMessages()
        const content = <div>{!_unreadMessages ? "Mark as unread" : "Mark as read"}</div>;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = rect.x;
        const y = rect.y + rect.height - 10;
        showTooltipAtXY(
            content, x, y,
            {
                padding: 10,
                width: 'max-content',
                backgroundColor: COLORS.white,
                transform: "translate(-100%)",
                border: `1px solid ${COLORS.primary_light_blue}`
            }
        );
    };

    const unreadMessages = props.getUnreadMessages()
    let backgroundColor = "transparent";
    if (unreadMessages)
        backgroundColor = "#fffaf0";
    if (props.isOpen)
        backgroundColor = COLORS.primary_light_blue;

    if (hideIcon())
        return <div className={`ll_v_c ${backgroundCell(backgroundColor)}`} style={{height: messagingTableRowHeight}}/>

    return <div className={`ll_v_c ${backgroundCell(backgroundColor)}`} style={{height: messagingTableRowHeight, justifyContent: "center"}}>
        <div onMouseEnter={showMarkAsReadTooltip}
             onMouseLeave={() => hideTooltip()}
             onClick={() => {
                 hideTooltip();
                 unreadMessages ? props.markAsRead() : props.markAsUnread();
             }}>
            <Badge
                show={!!unreadMessages}
                top={6}
                right={12}
                badge={
                    <div className={`${UnreadDot} ll_h_c`} style={{justifyContent: "center"}}>
                        {FONTS.reg_11_white(`${unreadMessages}`)}
                    </div>
                }>
                {ICONS.icon_envelope(undefined, 26)}
            </Badge>
        </div>
    </div>;
}
